/**
 * GoGo Ebikes — interactions + GSAP (safe boot)
 */
(() => {
  const doc = document.documentElement;
  doc.classList.remove('no-js');
  doc.classList.add('js');
  document.body.classList.add('gogo-theme');

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let mm;

  const showAllReveals = () => {
    document.querySelectorAll('.gogo-reveal, .gogo-img-reveal, .gogo-reveal-stagger').forEach((el) => {
      el.classList.add('is-visible');
      el.style.opacity = '';
      el.style.transform = '';
      el.style.filter = '';
      el.style.visibility = '';
    });
    document.querySelectorAll('[data-gogo-stagger] > *').forEach((el) => {
      el.style.opacity = '';
      el.style.transform = '';
      el.style.filter = '';
      el.style.visibility = '';
    });
    doc.classList.remove('gogo-motion-pending');
    if (typeof window.gsap !== 'undefined') {
      try {
        window.gsap.set('.gogo-reveal, .gogo-img-reveal, .gogo-reveal-stagger > *, [data-gogo-stagger] > *', {
          clearProps: 'opacity,transform,filter,visibility',
        });
      } catch (e) {
        /* ignore */
      }
    }
  };

  /* ——— Scroll progress + header ——— */
  let progress = document.querySelector('.gogo-scroll-progress');
  if (!progress) {
    progress = document.createElement('div');
    progress.className = 'gogo-scroll-progress';
    progress.innerHTML = '<span class="gogo-scroll-progress__bar"></span>';
    progress.setAttribute('aria-hidden', 'true');
    document.body.prepend(progress);
  }
  const progressBar = progress.querySelector('.gogo-scroll-progress__bar');

  const setScrolled = (y) => {
    document.body.classList.toggle('gogo-scrolled', y > 40);
  };

  let ticking = false;
  const onScrollFrame = () => {
    const max = doc.scrollHeight - window.innerHeight;
    const y = window.scrollY || 0;
    if (progressBar) progressBar.style.width = `${max > 0 ? (y / max) * 100 : 0}%`;
    setScrolled(y);
    ticking = false;
  };

  window.addEventListener(
    'scroll',
    () => {
      if (!ticking) {
        requestAnimationFrame(onScrollFrame);
        ticking = true;
      }
    },
    { passive: true }
  );
  onScrollFrame();

  /* ——— Hamburger open state ——— */
  const syncMenuIcon = () => {
    const open = Boolean(
      document.querySelector(
        'header-drawer details.menu-open, header-drawer details[open], .menu-drawer-container.menu-open, .menu-drawer-container[open]'
      )
    );
    document.querySelectorAll('header-drawer, .header-drawer').forEach((drawer) => {
      drawer.classList.toggle('is-open', open);
    });
    document.body.classList.toggle('gogo-menu-open', open);
  };
  document.addEventListener('toggle', syncMenuIcon, true);
  document.addEventListener('click', () => requestAnimationFrame(syncMenuIcon), true);
  syncMenuIcon();

  /* ——— CSS reveals: mark visible, never hide ——— */
  const initCssReveals = () => {
    document
      .querySelectorAll('.gogo-featured__media, .gogo-final__media')
      .forEach((el) => el.classList.add('gogo-img-reveal', 'gogo-reveal'));

    showAllReveals();

    document.querySelectorAll('.gogo-lifestyle').forEach((el) => {
      if (!('IntersectionObserver' in window)) {
        el.classList.add('is-inview');
        return;
      }
      const io2 = new IntersectionObserver(
        ([entry]) => el.classList.toggle('is-inview', entry.isIntersecting),
        { threshold: 0.25 }
      );
      io2.observe(el);
    });
  };

  /* ——— GSAP enhancements (optional) ——— */
  const initGsap = () => {
    if (typeof window.gsap === 'undefined' || reduceMotion) return;

    const gsap = window.gsap;
    try {
      if (window.ScrollTrigger) gsap.registerPlugin(ScrollTrigger);
    } catch (e) {
      return;
    }

    try {
      /* Hero entrance */
      document.querySelectorAll('[data-gogo-hero]').forEach((hero) => {
        const heading = hero.querySelector('[data-gogo-split]');
        const eyebrow = hero.querySelector('[data-gogo-hero-eyebrow]');
        const copy = hero.querySelector('[data-gogo-hero-copy]');
        const ctas = hero.querySelector('[data-gogo-hero-ctas]');
        const trust = hero.querySelector('[data-gogo-hero-trust]');
        const bg = hero.querySelector('[data-gogo-hero-parallax]');
        const scrollHint = hero.querySelector('.gogo-hero__scroll');
        const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

        try {
          if (heading && typeof window.SplitType !== 'undefined') {
            const split = new window.SplitType(heading, { types: 'words' });
            if (split.words && split.words.length) {
              gsap.fromTo(
                split.words,
                { yPercent: 80, opacity: 0 },
                {
                  yPercent: 0,
                  opacity: 1,
                  duration: 0.75,
                  stagger: 0.05,
                  ease: 'power3.out',
                  delay: 0.05,
                  onComplete: () => gsap.set(split.words, { clearProps: 'opacity,transform' }),
                }
              );
            }
          } else if (heading) {
            tl.from(heading, { y: 20, duration: 0.7, clearProps: 'transform' }, 0.05);
          }
        } catch (err) {
          /* keep heading visible */
        }

        if (eyebrow) tl.from(eyebrow, { y: 12, duration: 0.5, clearProps: 'transform' }, 0);
        if (copy) tl.from(copy, { y: 14, duration: 0.55, clearProps: 'transform' }, 0.2);
        if (ctas) tl.from(ctas.children, { y: 12, duration: 0.45, stagger: 0.06, clearProps: 'transform' }, 0.3);
        if (trust) tl.from(trust, { y: 8, duration: 0.4, clearProps: 'transform' }, 0.4);
        if (scrollHint) tl.from(scrollHint, { autoAlpha: 0, duration: 0.45 }, 0.5);

        if (bg && window.ScrollTrigger) {
          gsap.to(bg, {
            yPercent: 12,
            ease: 'none',
            scrollTrigger: {
              trigger: hero,
              start: 'top top',
              end: 'bottom top',
              scrub: true,
            },
          });
        }
      });

      /* Trust / stats stagger — soft fade without trapping opacity at 0 */
      if (window.ScrollTrigger && !reduceMotion) {
        document.querySelectorAll('[data-gogo-stagger]').forEach((grid) => {
          const items = Array.from(grid.children);
          if (!items.length) return;
          gsap.fromTo(
            items,
            { y: 16 },
            {
              y: 0,
              duration: 0.55,
              stagger: 0.08,
              ease: 'power2.out',
              immediateRender: false,
              scrollTrigger: {
                trigger: grid,
                start: 'top 92%',
                once: true,
              },
              onComplete: () => gsap.set(items, { clearProps: 'transform' }),
            }
          );
        });
      }
    } catch (e) {
      console.warn('[gogo] GSAP init skipped', e);
    }
  };

  const boot = () => {
    initCssReveals();
    initGsap();
  };

  const start = () => {
    boot();
    // Failsafe: never leave content invisible
    window.setTimeout(showAllReveals, 1200);
  };

  if (typeof window.gsap !== 'undefined') {
    start();
  } else {
    let tries = 0;
    const wait = window.setInterval(() => {
      tries += 1;
      if (typeof window.gsap !== 'undefined' || tries > 30) {
        window.clearInterval(wait);
        start();
      }
    }, 50);
  }

  document.addEventListener('shopify:section:load', () => {
    try {
      if (mm) mm.revert();
    } catch (e) {
      /* ignore */
    }
    if (window.ScrollTrigger) {
      try {
        ScrollTrigger.getAll().forEach((t) => t.kill());
      } catch (e) {
        /* ignore */
      }
    }
    boot();
    window.setTimeout(showAllReveals, 800);
  });

  window.addEventListener('pagehide', () => {
    try {
      if (window.ScrollTrigger) ScrollTrigger.getAll().forEach((t) => t.kill());
    } catch (e) {
      /* ignore */
    }
  });

  /* ——— FAQ / accordion ——— */
  document.querySelectorAll('[data-gogo-faq]').forEach((root) => {
    if (root.dataset.gogoFaqBound) return;
    root.dataset.gogoFaqBound = '1';
    const single = root.getAttribute('data-single') !== 'false';
    root.querySelectorAll('.gogo-faq__button').forEach((btn) => {
      btn.addEventListener('click', () => {
        const expanded = btn.getAttribute('aria-expanded') === 'true';
        const panelId = btn.getAttribute('aria-controls');
        const panel = panelId ? document.getElementById(panelId) : null;

        if (single) {
          root.querySelectorAll('.gogo-faq__button').forEach((other) => {
            if (other === btn) return;
            other.setAttribute('aria-expanded', 'false');
            const oid = other.getAttribute('aria-controls');
            const op = oid ? document.getElementById(oid) : null;
            if (op) {
              op.hidden = true;
              op.dataset.open = 'false';
            }
          });
        }

        btn.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        if (panel) {
          panel.hidden = expanded;
          panel.dataset.open = expanded ? 'false' : 'true';
        }
      });
    });
  });

  /* ——— Infinite marquee (testimonials + trust) ——— */
  document.querySelectorAll('[data-gogo-testimonials], [data-gogo-trust]').forEach((root) => {
    const marquee = root.querySelector('[data-gogo-marquee-root]');
    const track = root.querySelector('[data-gogo-marquee-track]');
    const group = root.querySelector('[data-gogo-marquee-group]');
    if (!marquee || !track || !group) return;
    if (track.children.length > 1) return;

    const autoplay = root.getAttribute('data-autoplay') !== 'false';
    const speed = Number(root.getAttribute('data-speed') || 45);
    marquee.style.setProperty('--gogo-marquee-duration', `${speed}s`);

    const makeClone = () => {
      const clone = group.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
      clone.querySelectorAll('[data-shopify-editor-block]').forEach((el) => {
        el.removeAttribute('data-shopify-editor-block');
      });
      return clone;
    };

    track.appendChild(makeClone());

    if (!reduceMotion && autoplay) marquee.classList.add('is-ready');

    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(
        ([entry]) => {
          if (!autoplay || reduceMotion) return;
          marquee.classList.toggle('is-paused', !entry.isIntersecting);
        },
        { threshold: 0.12 }
      );
      io.observe(marquee);
    }
  });

  /* ——— Forms ——— */
  document.querySelectorAll('form.gogo-waitlist, .gogo-newsletter form, .gogo-footer form').forEach((form) => {
    const status = form.querySelector('[data-gogo-status]');
    const email = form.querySelector('input[type="email"]');
    const submit = form.querySelector('[type="submit"]');

    form.addEventListener('submit', (e) => {
      if (!email) return;
      const value = email.value.trim();
      const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
      if (!valid) {
        e.preventDefault();
        if (status) {
          status.dataset.state = 'error';
          status.textContent = 'Please enter a valid email address.';
        }
        email.focus();
        return;
      }
      if (submit) {
        submit.disabled = true;
        submit.setAttribute('aria-busy', 'true');
      }
      if (status) {
        status.dataset.state = '';
        status.textContent = 'Joining the list…';
      }
    });
  });

  document.querySelectorAll('.gogo-contact-form form').forEach((form) => {
    const hp = form.querySelector('[name="contact[bot_check]"]');
    const submit = form.querySelector('[type="submit"]');
    form.addEventListener('submit', (e) => {
      if (hp && hp.value) {
        e.preventDefault();
        return;
      }
      if (submit) {
        submit.disabled = true;
        submit.setAttribute('aria-busy', 'true');
        const label = submit.querySelector('[data-label]');
        if (label) label.textContent = 'Sending…';
      }
    });
  });

  /* ——— Google reviews carousel ——— */
  const initGoogleReviews = (root) => {
    if (!root || root.dataset.gogoReviewsReady === 'true') return;
    root.dataset.gogoReviewsReady = 'true';

    const viewport = root.querySelector('[data-gogo-reviews-viewport]');
    const track = root.querySelector('[data-gogo-reviews-track]');
    const prev = root.querySelector('[data-gogo-reviews-prev]');
    const next = root.querySelector('[data-gogo-reviews-next]');
    if (!viewport || !track) return;

    const cards = Array.from(track.querySelectorAll('.gogo-greviews__card'));
    if (!cards.length) return;

    let index = 0;
    let timer = 0;
    const autoplay = root.getAttribute('data-autoplay') !== 'false';
    const delay = Math.max(3, Number(root.getAttribute('data-delay') || 5)) * 1000;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const gap = () => {
      const styles = window.getComputedStyle(track);
      return parseFloat(styles.columnGap || styles.gap || '14') || 14;
    };

    const step = () => cards[0].getBoundingClientRect().width + gap();

    const maxIndex = () => {
      const visible = Math.max(1, Math.floor((viewport.clientWidth + gap() * 0.25) / step()));
      return Math.max(0, cards.length - visible);
    };

    const render = () => {
      const max = maxIndex();
      if (index > max) index = 0;
      if (index < 0) index = max;
      track.style.transform = `translate3d(${-index * step()}px, 0, 0)`;
    };

    const go = (dir) => {
      const max = maxIndex();
      index += dir;
      if (index > max) index = 0;
      else if (index < 0) index = max;
      render();
    };

    prev?.addEventListener('click', (event) => {
      event.preventDefault();
      go(-1);
    });
    next?.addEventListener('click', (event) => {
      event.preventDefault();
      go(1);
    });

    root.querySelectorAll('[data-gogo-review-text]').forEach((text) => {
      const more = text.parentElement.querySelector('[data-gogo-review-more]');
      if (!more) return;
      if (text.scrollHeight <= text.clientHeight + 2) return;
      more.hidden = false;
      more.addEventListener('click', () => {
        const expanded = text.classList.toggle('is-expanded');
        more.textContent = expanded ? 'Show less' : 'Show more';
      });
    });

    const stop = () => {
      window.clearInterval(timer);
      timer = 0;
    };
    const start = () => {
      if (!autoplay || reduceMotion || timer) return;
      timer = window.setInterval(() => go(1), delay);
    };

    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', start);
    root.addEventListener('focusin', stop);
    root.addEventListener('focusout', start);
    window.addEventListener('resize', render);

    render();
    start();
  };

  document.querySelectorAll('[data-gogo-google-reviews]').forEach(initGoogleReviews);
  document.addEventListener('shopify:section:load', (event) => {
    initGoogleReviews(event.target.querySelector('[data-gogo-google-reviews]') || event.target);
  });

  /* ——— Sticky ATC ——— */
  const sticky = document.querySelector('[data-gogo-sticky-atc]');
  const mainBtn = document.querySelector(
    'product-form-component button[type="submit"], .product-form button[name="add"], form[action*="/cart/add"] button[type="submit"]'
  );
  if (sticky && mainBtn && 'IntersectionObserver' in window) {
    if (sticky.getAttribute('data-available') !== 'false') {
      const io = new IntersectionObserver(
        ([entry]) => sticky.classList.toggle('is-visible', !entry.isIntersecting),
        { threshold: 0 }
      );
      io.observe(mainBtn);
      sticky.querySelector('[data-gogo-sticky-trigger]')?.addEventListener('click', () => mainBtn.click());
    }
  }
})();
